import React from 'react';

function MedicalPrivacyPolicy() {
    return (
        <div className="container p-4">
            <p>
                <h3>MediCal - Privacy Policy</h3>

                Effective Date: 17 June 2025
                <br /><br />
                Welcome to MediCal, a medical calculator app created by Unit 326. This Privacy Policy explains how we
                collect, use, and protect your data when you use our app. We are committed to safeguarding your privacy
                and providing a safe, reliable experience. MediCal is designed to assist with medical calculations and
                is intended for informational and educational purposes only.
                <br /><br />

                <h3>Interpretation and Definitions</h3><br />

                <h5>Interpretation</h5>
                <p>The words of which the initial letter is capitalized have meanings defined under the following
                    conditions. The following definitions shall have the same meaning regardless of whether they appear
                    in singular or in plural.</p>

                <h6>Definitions</h6>
                <p>For the purposes of this Privacy Policy:</p>
                <ul>
                    <li>
                        <p><strong>Account</strong> means a unique account created for You to access our Service or
                            parts of our Service.</p>
                    </li>
                    <li>
                        <p><strong>Application</strong> refers to MediCal, the software program provided by the
                            Company.</p>
                    </li>
                    <li>
                        <p><strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our" in this
                            Agreement) refers to Unit 326, Joburg, South Africa.</p>
                    </li>
                    <li>
                        <p><strong>Country</strong> refers to: South Africa</p>
                    </li>
                    <li>
                        <p><strong>Device</strong> means any device that can access the Service such as a computer, a
                            cellphone or a digital tablet.</p>
                    </li>
                    <li>
                        <p><strong>Health Data</strong> means any medical values, calculation inputs, calculation
                            results, or other health-related information that You enter or that is generated through
                            use of the Service.</p>
                    </li>
                    <li>
                        <p><strong>Personal Data</strong> is any information that relates to an identified or
                            identifiable individual.</p>
                    </li>
                    <li>
                        <p><strong>Service</strong> refers to the Application.</p>
                    </li>
                    <li>
                        <p><strong>Service Provider</strong> means any natural or legal person who processes the data on
                            behalf of the Company, including third-party providers used to support the app's
                            functionality.</p>
                    </li>
                    <li>
                        <p><strong>Usage Data</strong> refers to data collected automatically, either generated by the
                            use of the Service or from the Service infrastructure itself.</p>
                    </li>
                    <li>
                        <p><strong>You</strong> means the individual accessing or using the Service, or the company, or
                            other legal entity on behalf of which such individual is accessing or using the Service, as
                            applicable.</p>
                    </li>
                </ul>

                <br /><br />

                <h3>1. Data We Collect</h3><br />

                <h5>Personal Data</h5>
                <p>While using Our Service, We may ask You to provide Us with certain personally identifiable
                    information. This may include, but is not limited to:</p>
                <ul>
                    <li>Email address</li>
                    <li>First name and last name</li>
                    <li>Profile information</li>
                    <li>Usage Data</li>
                </ul>

                <br />

                <h5>Health Data</h5>
                <p>MediCal collects and processes health-related inputs in order to provide its core medical calculator
                    features. This may include:</p>
                <ul>
                    <li>Patient demographic values such as age, weight, height, and sex</li>
                    <li>Clinical measurements and lab values entered for calculations (e.g., creatinine, blood
                        glucose, blood pressure, heart rate)</li>
                    <li>Calculation results and scores generated by the Service</li>
                    <li>Saved calculation history, if applicable</li>
                </ul>
                <p><strong>Important:</strong> MediCal processes Health Data locally on Your device wherever possible.
                    We do not require You to enter any patient-identifying information to perform calculations. We
                    strongly recommend that You do not enter any information that could identify a specific patient.</p>

                <br />

                <h5>Usage Data</h5>
                <p>Usage Data is collected automatically when using the Service. This may include Your device's IP
                    address, browser type, the pages of our Service that You visit, the time and date of Your visit,
                    time spent on those pages, unique device identifiers, and other diagnostic data.</p>

                <br /><br />

                <h3>2. How We Use Your Data</h3><br />
                <p>The Company may use Personal Data and Health Data for the following purposes:</p>

                <ul>
                    <li>
                        <p><strong>To provide and maintain our Service</strong>, including performing medical
                            calculations, generating results, and displaying calculation history.</p>
                    </li>
                    <li>
                        <p><strong>To manage Your Account</strong> and provide You access to all Service
                            functionalities.</p>
                    </li>
                    <li>
                        <p><strong>To improve our Service</strong>: aggregated and anonymized usage data may be used
                            to improve the accuracy and usability of our calculators and features.</p>
                    </li>
                    <li>
                        <p><strong>To contact You</strong> regarding updates, security notices, or important changes
                            to the Service.</p>
                    </li>
                    <li>
                        <p><strong>To manage Your requests</strong> and provide customer support.</p>
                    </li>
                    <li>
                        <p><strong>For analytics and service improvement</strong>: to identify usage trends and
                            evaluate and improve the Service.</p>
                    </li>
                </ul>

                <br />

                <h3>3. Medical Disclaimer</h3><br />
                <p>MediCal is a medical calculator tool designed for <strong>informational and educational purposes
                    only</strong>. The calculations, scores, and results provided by the Service do not constitute
                    professional medical advice, diagnosis, or treatment.</p>
                <p>The Service is intended to assist healthcare professionals and medical students with clinical
                    calculations. It should <strong>not</strong> be used as a substitute for professional medical
                    judgment. Always verify calculations independently and consult qualified healthcare professionals
                    for clinical decision-making.</p>
                <p>Unit 326 does not assume any responsibility for clinical decisions made based on results provided
                    by MediCal. Users are solely responsible for verifying the accuracy and appropriateness of any
                    calculation results before applying them in a clinical setting.</p>

                <br />

                <h3>4. Data Sharing</h3><br />
                <p>We may share Your information in the following situations:</p>
                <ul>
                    <li><strong>With Service Providers:</strong> We may share data with third-party providers who
                        assist in operating the Service, including cloud storage and analytics providers. These
                        providers are contractually obligated to handle Your data securely and only for the purposes
                        of providing services to Us.
                    </li>
                    <li><strong>For business transfers:</strong> We may share or transfer Your data in connection with
                        a merger, acquisition, or sale of Company assets.
                    </li>
                    <li><strong>With Affiliates:</strong> We may share Your information with Our affiliates, who are
                        required to honor this Privacy Policy.
                    </li>
                    <li><strong>With Your consent:</strong> We may disclose Your information for any other purpose
                        with Your explicit consent.
                    </li>
                </ul>
                <p>We <strong>do not sell</strong> Your personal data or Health Data to third parties.</p>

                <br />

                <h3>5. Data Security and Storage</h3><br />
                <p>We employ industry-standard security measures to protect Your data, including:</p>
                <ul>
                    <li>Encryption of data in transit and at rest</li>
                    <li>Restricted access to Your data, limited to authorized personnel only</li>
                    <li>Local processing of Health Data wherever possible to minimize data exposure</li>
                    <li>Compliance with applicable data protection regulations, including POPIA (South Africa),
                        GDPR, and CCPA</li>
                </ul>
                <p>Your data is stored securely on our servers. While we take all reasonable steps to protect Your
                    data, no method of transmission or storage is 100% secure.</p>

                <br />

                <h3>6. Data Retention</h3><br />
                <p>We retain Your Personal Data only for as long as is necessary to provide the Service and fulfill
                    the purposes set out in this Privacy Policy. Health Data entered for calculations is processed
                    in real-time and is not retained on our servers unless You explicitly choose to save calculation
                    history. When Your account is deleted, We will remove Your data in accordance with our data
                    deletion procedures, except where We are required to retain it by law.</p>

                <br />

                <h3>7. Your Privacy Rights</h3><br />
                <p>You have the right to:</p>
                <ul>
                    <li>Access the personal data We hold about You</li>
                    <li>Request correction of inaccurate data</li>
                    <li>Request deletion of Your data</li>
                    <li>Request deletion of any saved calculation history</li>
                    <li>Withdraw consent for data processing at any time</li>
                </ul>
                <p>To exercise these rights, visit the account settings section of the app or contact Us at <a
                    href="mailto:hello@unit326.com">hello@unit326.com</a>. You may also visit our data settings
                    page <a href="https://unit326.com/data-settings">here</a>.</p>

                <br />

                <h3>8. Third-Party Services</h3><br />
                <p>MediCal may allow You to sign in using Gmail or Apple account credentials. Please refer to their
                    respective privacy policies for information on how they handle Your data. We may also integrate
                    with third-party providers to support app functionality; these providers are bound by data
                    processing agreements with Us.</p>

                <br />

                <h3>9. Children's Privacy</h3><br />
                <p>MediCal is not intended for individuals under the age of 13. We do not knowingly collect personal
                    information from children. If You believe a child has provided Us with their personal information,
                    please contact Us at <a href="mailto:hello@unit326.com">hello@unit326.com</a> to have it
                    removed.</p>

                <br />

                <h3>10. Changes to This Privacy Policy</h3><br />
                <p>We reserve the right to modify this policy at any time. Changes will be effective upon posting
                    within the app or on our website. We encourage You to review this policy periodically. Continued
                    use of the Service after changes are posted constitutes Your acceptance of the revised policy.</p>

                <br />

                <h3>11. Contact Us</h3><br />
                <p>If you have any questions or concerns about this Privacy Policy, please contact us at:</p>
                <p>
                    <strong>Unit 326</strong><br />
                    Email: <a href="mailto:hello@unit326.com">hello@unit326.com</a><br />
                    Website: <a href="https://unit326.com/data-settings">unit326.com</a>
                </p>

                <br />
                Thank you for using MediCal. This app is intended for informational and educational purposes only
                and should not replace professional medical advice.
                <br /><br />
                Unit 326
            </p>
        </div>
    );
}

export default MedicalPrivacyPolicy;
